import type { ComponentProps } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import { Heading } from "common/core/typography";
import Button from "common/core/button";
import Link from "common/core/link";
import GoogleSignInButton from "common/account/google_signin_button";
import Icon from "common/core/icon";
import { useProofRebrand } from "util/feature_detection";
import { AccountBlurb } from "common/proof_frame/global_nav/proof_id_widget";

import Styles from "./index.module.scss";

export function LoginHeader({ liveRegion }: { liveRegion?: React.AriaAttributes["aria-live"] }) {
  const isProof = useProofRebrand();

  return (
    <div aria-live={liveRegion}>
      {isProof ? (
        <>
          <Heading level="h1" textStyle="subtitle" textAlign="left" className={Styles.mainHeading}>
            <FormattedMessage
              id="02560a61-63ba-4703-9eea-34bdb25eb8a0"
              defaultMessage="Log in to your account"
            />
          </Heading>
          <AccountBlurb />
        </>
      ) : (
        <>
          <Heading
            level="h1"
            textStyle="headingThree"
            textAlign="center"
            className={classnames(Styles.mainHeading, Styles.deprecatedHeading)}
          >
            <FormattedMessage
              id="dce7a702-c9b1-4a8b-b923-aaa693a0a9ce"
              defaultMessage="Welcome back!"
            />
          </Heading>
          <Heading
            level="h2"
            textStyle="headingFive"
            textAlign="center"
            className={Styles.secondaryHeading}
          >
            <FormattedMessage
              id="f95076a0-5640-4a4b-8710-13da491c6929"
              defaultMessage="Log in to your Notarize account"
            />
          </Heading>
        </>
      )}
    </div>
  );
}

export function FooterLink(props: ComponentProps<typeof Link>) {
  return (
    <span className={Styles.footerPortalLink}>
      <Link {...props} />
    </span>
  );
}

export function FooterButton(props: ComponentProps<typeof Button>) {
  return (
    <span className={Styles.footerPortalLink}>
      <Button {...props} />
    </span>
  );
}
type BackButtonProps = {
  onClick: () => void;
  ["aria-label"]?: string;
};

export function BackButton({ onClick, "aria-label": ariaLabel }: BackButtonProps) {
  return (
    <FooterButton variant="tertiary" buttonColor="action" onClick={onClick} aria-label={ariaLabel}>
      <>
        <Icon name="arrow-left" className={Styles.icon} />
        <FormattedMessage id="0270eafa-929f-47a1-b036-8a6d73fde2b1" defaultMessage="Go back" />
      </>
    </FooterButton>
  );
}

export function BackButtonOnlyFooter(props: BackButtonProps) {
  return (
    <div className={Styles.footerCentered}>
      <BackButton {...props} />
    </div>
  );
}

type ContinueButtonProps = {
  onClick?: () => void;
  disabled?: boolean;
};

export function Continue({ onClick, disabled }: ContinueButtonProps) {
  return (
    <Button
      onClick={onClick}
      type="submit"
      disabled={disabled}
      buttonColor="action"
      buttonSize="large"
      variant="primary"
      fullwidth
    >
      <FormattedMessage id="2bae9507-222d-4a90-aaf5-5d6c0f87fea2" defaultMessage="Continue" />
    </Button>
  );
}

type GoogleSignInButtonType = {
  onGoogleSignIn: (authCode: string) => void;
};

export function SignInWithGoogle({ onGoogleSignIn }: GoogleSignInButtonType) {
  return <GoogleSignInButton signIn={onGoogleSignIn} />;
}
